import { Neo4jGraphQL } from '@neo4j/graphql';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { type ASTVisitor, type DirectiveNode, GraphQLError, parse } from 'graphql';
import type { SDLValidationContext } from 'graphql/validation/ValidationContext';
import { validateSDL } from 'graphql/validation/validate';
import type * as yup from 'yup';

import { SchemaValidationError } from './validation-errors';

export type ValidationError = {
  message: string;
  error: yup.ValidationError;
};

type Path<T, Prefix extends string = ''> = {
  [K in keyof T]: T[K] extends object
    ? T[K] extends (infer U)[]
      ?
          | `${Prefix}${K & string}.general`
          | `${Prefix}${K & string}[${number}]`
          | Path<U, `${Prefix}${K & string}[${number}].`>
      : `${Prefix}${K & string}` | Path<T[K], `${Prefix}${K & string}.`>
    : `${Prefix}${K & string}` | `${Prefix}${K & string}[${number}]`;
}[keyof T];

export type Validation<T> = {
  [P in Path<T>]?: ValidationError;
};

export const validateYup = <T>(schema: yup.AnySchema, data: T, onlyRequired = false): Validation<T> | null => {
  let validation = null;
  try {
    // eslint-disable-next-line no-sync
    schema.validateSync(data, { abortEarly: false });
  } catch (e) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const error: yup.ValidationError = e as yup.ValidationError;

    validation = error.inner.reduce((acc, err) => {
      if ((onlyRequired && err.type !== 'required') || isNullish(err.path)) {
        return acc;
      }
      if (err.type === 'at-least-one-auth-provider-enabled') {
        return { ...acc, 'authProviders.general': { message: err.message, error: err } };
      }

      return { ...acc, [err.path]: { message: err.message, error: err } };
    }, {});

    if (Object.keys(validation).length === 0) {
      validation = null;
    }
  }

  return validation;
};

function isGraphQLError(error: unknown): error is GraphQLError {
  return error instanceof Error && 'locations' in error;
}

const PROHIBITED_DIRECTIVE_ERROR = 'prohibited-directive';

function NoProhibitedDirectivesValidator(directives: string[]) {
  return (context: SDLValidationContext): ASTVisitor => {
    return {
      Directive(directiveNode: DirectiveNode) {
        if (directives.includes(directiveNode.name.value)) {
          context.reportError(
            new GraphQLError(
              `The following directive cannot be use in an Aura Data API: "@${directiveNode.name.value}"`,
            ),
          );
        }
      },
    };
  };
}

const prohibitedDirectives = ['customResolver', 'populatedBy', 'subscription', 'subscriptionAuthorization', 'vector'];

function validateNoProhibitedDirectives(typeDefs: string): GraphQLError[] {
  if (typeDefs.length === 0) {
    throw new SchemaValidationError('Invalid type definitions');
  }
  const typeDefsDocument = parse(typeDefs);
  const prohibitedDirectiveErrors = validateSDL(typeDefsDocument, undefined, [
    NoProhibitedDirectivesValidator(prohibitedDirectives),
  ]);
  if (prohibitedDirectiveErrors.length > 0) {
    return prohibitedDirectiveErrors.map((err) => new SchemaValidationError(err.message, PROHIBITED_DIRECTIVE_ERROR));
  }
  return [];
}

export const validateTypeDefinitions = async (
  typeDefs: string,
  isFederationEnabled: boolean | undefined,
): Promise<GraphQLError[]> => {
  const errors: GraphQLError[] = [];

  try {
    const options = {
      typeDefs,
      driver: undefined,
      validate: true,
      features: {
        filters: {
          String: {
            MATCHES: false,
          },
          ID: {
            MATCHES: false,
          },
        },
        excludeDeprecatedFields: {
          mutationOperations: true,
          aggregationFilters: true,
          relationshipFilters: true,
          attributeFilters: true,
        },
      },
      debug: false,
    };

    errors.push(...validateNoProhibitedDirectives(typeDefs));

    const neoSchema = new Neo4jGraphQL(options);

    if (isNotNullish(isFederationEnabled) && isFederationEnabled) {
      await neoSchema.getSubgraphSchema();
    } else {
      await neoSchema.getSchema();
    }
  } catch (error) {
    if (Array.isArray(error)) {
      if (error.every(isGraphQLError)) {
        errors.push(...error);
      }
    } else if (isGraphQLError(error)) {
      errors.push(error);
    }
  }

  return errors;
};
